import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { motion, useIsPresent, useReducedMotion } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import useDialog from '../../../hooks/useDialog';
export default function CheckoutDialog({ label, closeLabel, closeDisabled, onClose, returnFocus, contentClassName, footer, children, }) {
    const container = useRef(null);
    const dialog = useRef(null);
    const content = useRef(null);
    const closeButton = useRef(null);
    const isPresent = useIsPresent();
    const reducedMotion = useReducedMotion();
    const isDesktop = useMediaQuery({ minWidth: 992 });
    const hiddenPosition = { x: isDesktop ? '100%' : 0, y: isDesktop ? 0 : '100%' };
    useEffect(() => {
        const layer = container.current;
        const scrollArea = content.current;
        const viewport = window.visualViewport;
        if (!layer || !scrollArea || !viewport)
            return undefined;
        const revealFocusedInput = () => {
            const focused = document.activeElement;
            if (!(focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) ||
                !scrollArea.contains(focused)) {
                return;
            }
            const field = focused.getBoundingClientRect();
            const area = scrollArea.getBoundingClientRect();
            // Center clipped fields in this panel only, keeping the page behind it locked.
            if (field.bottom > area.bottom - 8 || field.top < area.top + 8) {
                scrollArea.scrollTop += (field.top + field.bottom - area.top - area.bottom) / 2;
            }
        };
        const updateViewport = () => {
            layer.style.height = `${viewport.height}px`;
            layer.style.top = `${viewport.offsetTop}px`;
            revealFocusedInput();
        };
        updateViewport();
        scrollArea.addEventListener('focusin', revealFocusedInput);
        viewport.addEventListener('resize', updateViewport);
        viewport.addEventListener('scroll', updateViewport);
        return () => {
            scrollArea.removeEventListener('focusin', revealFocusedInput);
            viewport.removeEventListener('resize', updateViewport);
            viewport.removeEventListener('scroll', updateViewport);
        };
    }, []);
    useDialog({
        dialog,
        container,
        initialFocus: closeButton,
        onClose: () => {
            if (isPresent)
                onClose();
        },
        restoreFocusTo: returnFocus,
        bodyClassName: 'opc-open',
    });
    return (_jsxs("div", { ref: container, className: "opc-layer position-fixed", inert: !isPresent, children: [_jsx(motion.button, { type: "button", className: "opc-backdrop position-absolute border-0 cursor-default", tabIndex: -1, "aria-label": closeLabel, disabled: closeDisabled, onClick: onClose, initial: reducedMotion ? false : { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: reducedMotion ? 0 : 0.45, ease: 'linear' } }), _jsxs(motion.aside, { ref: dialog, className: "opc-panel position-absolute d-flex flex-column bg-light", role: "dialog", tabIndex: -1, "aria-modal": "true", "aria-label": label, initial: reducedMotion ? false : hiddenPosition, animate: { x: 0, y: 0 }, exit: reducedMotion ? { x: 0, y: 0 } : hiddenPosition, transition: { duration: reducedMotion ? 0 : 0.45, ease: [0.25, 0.1, 0.25, 1] }, children: [_jsxs("div", { ref: content, className: ['opc-panel-content flex-fill min-height-0 min-w-0', contentClassName].filter(Boolean).join(' '), children: [_jsx("div", { className: "opc-close-row position-relative", children: _jsx("button", { ref: closeButton, type: "button", className: "opc-close position-absolute p-0 border-0 bg-transparent text-dark line-height-none cursor-pointer", "aria-label": closeLabel, disabled: closeDisabled, onClick: onClose, children: _jsx("i", { className: "nbw-icon nbw-icon-close", "aria-hidden": "true" }) }) }), children] }), _jsx("div", { className: "opc-panel-footer position-relative flex-none bg-light", children: footer })] })] }));
}
